/* ==========================================================================
   cute.js — Cyber-fairy homepage interactions
   Mirrors the Framer-motion behaviour of the reference site:
     - hero parallax on scroll + mouse
     - twinkling star field
     - magnetic pill buttons
     - scroll-reveal (IntersectionObserver)
     - story-image parallax
   ========================================================================== */

(function () {
  'use strict';

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Scroll reveal ---------- */
  const initReveal = () => {
    const items = document.querySelectorAll('.ct-reveal');
    if (!items.length) return;

    if (!('IntersectionObserver' in window) || REDUCED) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.06 }
    );

    items.forEach((el, i) => {
      // stagger siblings that share the same parent
      const parent = el.parentElement;
      if (parent) {
        const sibs = Array.from(parent.children).filter((c) => c.classList.contains('ct-reveal'));
        const idx = sibs.indexOf(el);
        if (idx > 0) el.style.transitionDelay = (idx * 90) + 'ms';
      }
      io.observe(el);
    });
  };

  /* ---------- Twinkling stars ---------- */
  const initStars = () => {
    const host = document.querySelector('[data-ct-stars]');
    if (!host || REDUCED) return;

    const frag = document.createDocumentFragment();
    for (let i = 0; i < 30; i++) {
      const star = document.createElement('div');
      star.className = 'ct-hero__star';
      star.style.left = (Math.random() * 100) + '%';
      star.style.top = (Math.random() * 100) + '%';
      const size = Math.random() * 3 + 1;
      star.style.width = size + 'px';
      star.style.height = size + 'px';
      star.style.opacity = (Math.random() * 0.5 + 0.1).toFixed(2);
      star.style.animationDelay = (Math.random() * 4).toFixed(2) + 's';
      star.style.animationDuration = (Math.random() * 2 + 2).toFixed(2) + 's';
      frag.appendChild(star);
    }
    host.appendChild(frag);
  };

  /* ---------- Hero parallax (scroll + mouse) ---------- */
  const initHero = () => {
    const hero = document.querySelector('[data-ct-hero]');
    if (!hero || REDUCED) return;

    const bg = hero.querySelector('[data-ct-hero-bg]');
    const content = hero.querySelector('[data-ct-hero-content]');
    const glow = hero.querySelector('.ct-hero__glow');

    let mouseX = 0;
    let mouseY = 0;
    let curX = 0;
    let curY = 0;
    let scrollY = 0;

    const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

    const onMove = (e) => {
      mouseX = (e.clientX / window.innerWidth - 0.5) * 2;
      mouseY = (e.clientY / window.innerHeight - 0.5) * 2;
    };

    const onScroll = () => {
      scrollY = window.scrollY;
    };

    const tick = () => {
      curX += (mouseX - curX) * 0.08;
      curY += (mouseY - curY) * 0.08;

      if (bg) {
        const scale = 1 + Math.min(scrollY, 800) / 800 * 0.06;
        bg.style.transform = 'scale(' + scale.toFixed(4) + ')';
      }
      if (content) {
        const tx = clamp(curX * -8, -8, 8);
        const ty = clamp(curY * -6 + scrollY * -0.12, -40, 40);
        content.style.transform = 'translate3d(' + tx.toFixed(2) + 'px,' + ty.toFixed(2) + 'px,0)';
        content.style.opacity = String(clamp(1 - scrollY / 400, 0, 1));
      }
      if (glow) {
        glow.style.transform = 'translate3d(' + (curX * 8).toFixed(2) + 'px,' + (curY * 6).toFixed(2) + 'px,0)';
      }
      requestAnimationFrame(tick);
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    requestAnimationFrame(tick);
  };

  /* ---------- Story image parallax ---------- */
  const initParallax = () => {
    const items = document.querySelectorAll('[data-ct-parallax]');
    if (!items.length || REDUCED) return;

    const update = () => {
      const vh = window.innerHeight;
      items.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > vh + 200) return;
        // progress: 0 when entering, 1 when leaving
        const p = (vh - rect.top) / (vh + rect.height);
        const shift = (p - 0.5) * 100;
        el.style.transform = 'translate3d(0,' + shift.toFixed(2) + 'px,0)';
      });
    };

    let raf = null;
    window.addEventListener('scroll', () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        update();
        raf = null;
      });
    }, { passive: true });

    update();
  };

  /* ---------- Magnetic buttons ---------- */
  const initMagnetic = () => {
    const els = document.querySelectorAll('[data-ct-magnetic]');
    if (!els.length || REDUCED) return;

    els.forEach((el) => {
      const strength = 0.25;
      let raf = null;
      let tx = 0;
      let ty = 0;
      let cx = 0;
      let cy = 0;

      const move = (e) => {
        const rect = el.getBoundingClientRect();
        tx = (e.clientX - (rect.left + rect.width / 2)) * strength;
        ty = (e.clientY - (rect.top + rect.height / 2)) * strength;
        if (!raf) raf = requestAnimationFrame(loop);
      };

      const loop = () => {
        cx += (tx - cx) * 0.2;
        cy += (ty - cy) * 0.2;
        el.style.transform = 'translate3d(' + cx.toFixed(2) + 'px,' + cy.toFixed(2) + 'px,0)';
        if (Math.abs(tx - cx) > 0.1 || Math.abs(ty - cy) > 0.1) {
          raf = requestAnimationFrame(loop);
        } else {
          raf = null;
        }
      };

      const reset = () => {
        tx = 0;
        ty = 0;
        if (!raf) raf = requestAnimationFrame(loop);
      };

      el.addEventListener('mousemove', move);
      el.addEventListener('mouseleave', reset);
    });
  };

  const init = () => {
    initReveal();
    initStars();
    initHero();
    initParallax();
    initMagnetic();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
